import type { TaskDefinitionInput } from './types.ts'

/**
 * Valida una definicion de tarea contra las reglas del dominio (§6.5, §6.7).
 * Devuelve la lista de problemas; vacia significa valida.
 *
 * Se ejecuta en el servidor antes de guardar. El formulario del admin puede
 * usar lo mismo para avisar mientras se escribe, pero la validacion que cuenta
 * es la del servidor.
 */
export function validateDefinition(def: TaskDefinitionInput): string[] {
  const errors: string[] = []

  // Un bonus abierto se lo lleva quien lo reclame; una tarea obligatoria
  // abierta no la reclamaria nadie nunca y quedaria siempre sin hacer.
  if (def.kind === 'DEDUCTIBLE' && def.assignmentMode === 'OPEN') {
    errors.push('Una tarea obligatoria no puede ser de asignacion abierta: nadie reclamaria una penalizacion')
  }

  if (def.kind === 'DEDUCTIBLE') {
    const weight = def.weight ?? 1
    if (!Number.isInteger(weight) || weight < 1) {
      errors.push(`El peso debe ser un entero >= 1 (recibido ${weight})`)
    }
    if (def.bonusAmountCents != null) {
      errors.push('Una tarea obligatoria no lleva importe de bonus: su valor sale del reparto semanal')
    }
  }

  if (def.kind === 'BONUS') {
    if (!Number.isInteger(def.bonusAmountCents) || (def.bonusAmountCents ?? 0) <= 0) {
      errors.push('Un bonus necesita un importe fijo mayor que cero')
    }
    if (def.weight != null && def.weight !== 1) {
      errors.push('Un bonus no usa peso: no participa en el reparto de la asignacion')
    }
  }

  switch (def.assignmentMode) {
    case 'FIXED': {
      const assignees = def.assigneeIds ?? []
      if (assignees.length === 0) {
        errors.push('Una tarea fija necesita al menos un responsable')
      }
      if (new Set(assignees).size !== assignees.length) {
        errors.push('Hay responsables repetidos')
      }
      break
    }
    case 'ROTATING': {
      const members = def.rotationMembers ?? []
      if (members.length < 2) {
        errors.push('Una tarea rotativa necesita al menos dos participantes')
      }
      if (new Set(members).size !== members.length) {
        errors.push('Hay participantes repetidos en la rotacion')
      }
      if (!def.rotationAnchorDate) {
        errors.push('Una tarea rotativa necesita una fecha de inicio de ciclo')
      }
      break
    }
    case 'OPEN':
      if ((def.openCandidates ?? []).length < 1) {
        errors.push('Un bonus abierto necesita al menos un candidato')
      }
      break
  }

  switch (def.recurrence) {
    case 'ONE_OFF':
      if (!def.oneOffDate) errors.push('Una tarea puntual necesita una fecha')
      break
    case 'WEEKLY_ANY':
      if ((def.daysOfWeek ?? []).length > 0) {
        errors.push('Una tarea de cualquier dia de la semana no lleva dias concretos')
      }
      break
    case 'ON_DEMAND':
      // No lleva ni fecha ni dias: existe hasta que alguien la activa.
      break
    case 'WEEKLY':
      if ((def.daysOfWeek ?? []).length !== 1) {
        errors.push('Una tarea semanal debe indicar exactamente un dia')
      }
      break
    case 'DAILY':
      if ((def.daysOfWeek ?? []).length === 0) break // sin dias = los 7
      if ((def.daysOfWeek ?? []).some((d) => d < 1 || d > 7)) {
        errors.push('Los dias de la semana deben estar entre 1 (lunes) y 7 (domingo)')
      }
      break
  }

  return errors
}

export function assertValidDefinition(def: TaskDefinitionInput): void {
  const errors = validateDefinition(def)
  if (errors.length > 0) {
    throw new Error(`Definicion de tarea invalida:\n- ${errors.join('\n- ')}`)
  }
}
